import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import { I18nProvider } from "./i18n";
import { AuthProvider } from "./AuthContext";

import Landing from "./pages/Landing";
import Pricing from "./pages/Pricing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
import Directory from "./pages/Directory";
import ManufacturerPublic from "./pages/ManufacturerPublic";
import AdminVault from "./pages/AdminVault";
import AdminControls from "./pages/AdminControls";
import Dashboard from "./pages/Dashboard";
import ProfileEditor from "./pages/ProfileEditor";
import Documents from "./pages/Documents";
import Inquiries from "./pages/Inquiries";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import ShareView from "./pages/ShareView";
import Admin from "./pages/Admin";
import Founding from "./pages/Founding";
import BuyerPortal from "./pages/BuyerPortal";
import SalesKit from "./pages/SalesKit";
import Digest from "./pages/Digest";
import Guides from "./pages/Guides";
import Capability from "./pages/Capability";
import About from "./pages/About";
import Terms from "./pages/legal/Terms";
import Privacy from "./pages/legal/Privacy";
import Refund from "./pages/legal/Refund";
import LegalIndex from "./pages/legal/LegalIndex";
import LetterOfListing from "./pages/LetterOfListing";
import ROICalculator from "./pages/ROICalculator";
import ErrorBoundary from "./components/ErrorBoundary";
import SalesToolkit from "./pages/SalesToolkit";
import Plants from "./pages/Plants";
import PlantProfile from "./pages/PlantProfile";
import AdminRoadmap from "./pages/AdminRoadmap";
import AdminWebhooks from "./pages/AdminWebhooks";
import AdminSuppliers from "./pages/AdminSuppliers";
import AdminSeo from "./pages/AdminSeo";
import SupplierPublic from "./pages/SupplierPublic";
import ClaimConfirm from "./pages/ClaimConfirm";
import CookieBanner from "./components/CookieBanner";
import CorredorChat from "./components/CorredorChat";

function AppRouter() {
  const location = useLocation();
  // Detect OAuth callback hash synchronously — BEFORE ProtectedRoute mounts
  if (typeof window !== "undefined" && location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <Nav />
      <main className="min-h-[60vh]">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/directory" element={<Directory />} />
          <Route path="/plants" element={<Plants />} />
          <Route path="/plants/:slug" element={<PlantProfile />} />
          <Route path="/digest" element={<Digest />} />
          <Route path="/guides" element={<Guides />} />
          <Route path="/guides/:slug" element={<Guides />} />
          <Route path="/c/:slug" element={<Capability />} />
          <Route path="/m/:slug" element={<ManufacturerPublic />} />
          <Route path="/claim/confirm/:token" element={<ClaimConfirm />} />
          <Route path="/suppliers/:slug" element={<SupplierPublic />} />
          <Route path="/share/:token" element={<ShareView />} />
          <Route path="/buyer/:token" element={<BuyerPortal />} />
          <Route path="/founding" element={<Founding />} />
          <Route path="/sales-kit" element={<SalesKit />} />
          <Route path="/about" element={<About />} />
          <Route path="/roi" element={<ErrorBoundary fallback="The ROI calculator hit an unexpected error. Tap Retry to continue."><ROICalculator /></ErrorBoundary>} />
          <Route path="/sales-toolkit" element={<SalesToolkit />} />
          <Route path="/legal" element={<LegalIndex />} />
          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/legal/refund" element={<Refund />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfileEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents"
            element={
              <ProtectedRoute>
                <Documents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inquiries"
            element={
              <ProtectedRoute>
                <Inquiries />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/letter"
            element={
              <ProtectedRoute>
                <LetterOfListing />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription/success"
            element={
              <ProtectedRoute>
                <SubscriptionSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/roadmap"
            element={
              <ProtectedRoute requireAdmin>
                <AdminRoadmap />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/webhooks"
            element={
              <ProtectedRoute requireAdmin>
                <AdminWebhooks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/suppliers"
            element={
              <ProtectedRoute requireAdmin>
                <AdminSuppliers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/seo"
            element={
              <ProtectedRoute requireAdmin>
                <AdminSeo />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/vault"
            element={
              <ProtectedRoute requireAdmin>
                <AdminVault />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/controls"
            element={
              <ProtectedRoute requireAdmin>
                <AdminControls />
              </ProtectedRoute>
            }
          />
        </Routes>
      </main>
      <Footer />
      <CookieBanner />
      <CorredorChat />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <I18nProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        </BrowserRouter>
      </I18nProvider>
    </div>
  );
}

export default App;
